import { useIsMutating } from '@tanstack/react-query';
import { getRepositoryQueryKey } from '@mews-commander/data-access';
import { SmallContentContainer, LocalizedTypography } from '@mews-commander/ui';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Container, Link, Stack } from '@optimus-web/core';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { DescriptionSection } from './components/DescriptionSection';
import { ConfigurationSection } from './components/ConfigurationSection';
import { useQueryMulticurrencyConfiguration } from './hooks/useQueryMulticurrencyConfiguration';

const MULTICURRENCY_LEGAL_LINK_ENG = 'https://www.mews.com/en/legal/multicurrency';

export const MulticurrencySettings = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();

    const isMutatingMulticurrencyConfiguration =
        useIsMutating({
            mutationKey: getRepositoryQueryKey('multiCurrencyServiceConfiguration', 'update'),
        }) > 0;

    const isMutatingMulticurrencyEarlyOptIn =
        useIsMutating({
            mutationKey: getRepositoryQueryKey('multiCurrencyServiceConfiguration', 'earlyOptIn'),
        }) > 0;

    const isMutating = isMutatingMulticurrencyConfiguration || isMutatingMulticurrencyEarlyOptIn;

    const {
        isMulticurrencyServiceEnabled,
        isDccMulticurrencyServiceEnabled,
        isFxMulticurrencyServiceEnabled,
        isDccMulticurrencyServiceAvailable,
        multicurrencyStartDate,
        isFetching,
    } = useQueryMulticurrencyConfiguration({
        enterpriseId: currentEnterpriseId,
    });

    return (
        <SmallContentContainer align="left">
            <Stack vertical spacing="500">
                <DescriptionSection
                    isMulticurrencyServiceEnabled={isMulticurrencyServiceEnabled}
                    isDccMulticurrencyServiceEnabled={isDccMulticurrencyServiceEnabled}
                    isFxMulticurrencyServiceEnabled={isFxMulticurrencyServiceEnabled}
                    isDccMulticurrencyServiceAvailable={isDccMulticurrencyServiceAvailable}
                    multicurrencyStartDate={multicurrencyStartDate}
                    isFetching={isFetching || isMutating}
                />
                <ConfigurationSection
                    isMulticurrencyServiceEnabled={isMulticurrencyServiceEnabled}
                    isDccMulticurrencyServiceEnabled={isDccMulticurrencyServiceEnabled}
                    isFxMulticurrencyServiceEnabled={isFxMulticurrencyServiceEnabled}
                    isDccMulticurrencyServiceAvailable={isDccMulticurrencyServiceAvailable}
                    multicurrencyStartDate={multicurrencyStartDate}
                    isFetching={isFetching || isMutating}
                />
            </Stack>

            <Container padding={['300', '0']}>
                <LocalizedTypography
                    as="div"
                    textKey={TextKey.MulticurrencySettingsLinkTermsAndConditions}
                    textStyle="bodySmall"
                    variant="secondary"
                    substitutes={{
                        a: (text) => (
                            <Link
                                data-test-id={DataTestId.MulticurrencySettingsLinkTermsAndConditions}
                                href={MULTICURRENCY_LEGAL_LINK_ENG}
                                rel="noreferrer"
                                target="_blank"
                                variant="basic"
                            >
                                {text}
                            </Link>
                        ),
                    }}
                />
            </Container>
        </SmallContentContainer>
    );
};
